<script setup lang="ts">
import TrustBadges from '@/shared/components/TrustBadges.vue'
// Pixabay Content License (free commercial use, no attribution required).
import heroDusk from '@/assets/ror-hero-dusk.jpg'

const emit = defineEmits<{ start: []; browse: []; replace: [] }>()
</script>

<template>
    <section id="top" class="site-hero site-section hero-photo-bg" :style="{ '--hero-photo': `url(${heroDusk})` }">
        <div class="decor" aria-hidden="true">
            <span class="orb orb-a"></span>
            <span class="orb orb-b"></span>
            <svg class="pulse" viewBox="0 0 1440 200" fill="none" preserveAspectRatio="none">
                <path
                    d="M-20 120 H 420 L 470 60 L 540 170 L 600 90 L 650 120 H 1460"
                    stroke="#3ddc84"
                    stroke-opacity="0.12"
                    stroke-width="2"
                    stroke-linejoin="round"
                />
            </svg>
        </div>
        <div class="site-content hero-grid">
            <div>
                <span class="badge">AI-Powered Second Opinion</span>
                <h1>Repair or Replace?</h1>
                <p class="lede muted">
                    Get an unbiased, AI-powered HVAC opinion in under two minutes. We're an
                    educational resource — not a contractor — so the answer you get is the answer
                    the evidence supports.
                </p>
                <div class="actions">
                    <button class="cta" type="button" @click="emit('start')">Start Free Diagnosis</button>
                    <button class="cta cta--secondary" type="button" @click="emit('browse')">
                        Browse Common Problems
                    </button>
                    <button class="cta cta--quiet" type="button" @click="emit('replace')">
                        I already know I need a replacement →
                    </button>
                </div>
                <TrustBadges class="badges" :badges="['Serving Northeast Florida', '100% Free', 'No Signup Required', 'AI Powered', 'Privacy Focused']" />
            </div>
            <div class="hero-art glass" aria-hidden="true">
                <div class="dial">
                    <div class="dial-arc"></div>
                    <span class="dial-label">repair&nbsp;·&nbsp;replace</span>
                </div>
                <p class="muted small">Honest advice. Data-driven. No sales pressure.</p>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
// Parallax photographic banner: a dusk home under the charcoal wash. The
// theme gradient + grid pattern still layer on top via .site-hero.
.hero-photo-bg {
    // NOTE: the theme's --site-gradient-hero ends in an OPAQUE charcoal
    // layer, so it can't sit above the photo — only the green tint does.
    background-image: var(--site-pattern, none),
        radial-gradient(120% 90% at 70% 10%, rgba(61, 220, 132, 0.1) 0%, rgba(19, 21, 25, 0) 55%),
        linear-gradient(100deg, rgba(19, 21, 25, 0.94) 0%, rgba(19, 21, 25, 0.74) 45%, rgba(19, 21, 25, 0.4) 100%),
        linear-gradient(180deg, rgba(19, 21, 25, 0.2) 55%, rgba(19, 21, 25, 0.96) 100%),
        var(--hero-photo);
    background-size: auto, auto, auto, auto, cover;
    background-position: center;

    @media (min-width: 900px) and (prefers-reduced-motion: no-preference) {
        background-attachment: scroll, scroll, scroll, fixed, fixed;
    }
}

.orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);

    &.orb-a {
        width: 36rem;
        height: 36rem;
        top: -14rem;
        left: -10rem;
        background: radial-gradient(circle, rgba(61, 220, 132, 0.14) 0%, rgba(61, 220, 132, 0) 70%);
    }

    &.orb-b {
        width: 30rem;
        height: 30rem;
        bottom: -12rem;
        right: -8rem;
        background: radial-gradient(circle, rgba(41, 184, 168, 0.12) 0%, rgba(41, 184, 168, 0) 70%);
    }
}

// A heartbeat/pulse line — the diagnostic motif, barely there.
.pulse {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 8%;
    width: 100%;
    height: 200px;
}

.hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    gap: var(--site-space-2xl);
    align-items: center;

    @media (max-width: 860px) {
        grid-template-columns: 1fr;
    }
}

.lede {
    font-size: 1.15rem;
    max-width: 34rem;
}

.actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);
    margin: var(--site-space-lg) 0;
}

.badges {
    margin-top: var(--site-space-md);
}

.hero-art {
    padding: var(--site-space-xl);
    text-align: center;

    .small {
        margin: 0;
        font-size: 0.9rem;
    }
}

.dial {
    position: relative;
    width: 190px;
    height: 120px;
    margin: 0 auto var(--site-space-md);
    overflow: hidden;

    .dial-arc {
        width: 190px;
        height: 190px;
        border-radius: 50%;
        border: 14px solid var(--site-border);
        border-top-color: var(--site-accent);
        border-right-color: var(--site-accent);
        transform: rotate(-45deg);
        box-shadow: var(--site-glow);
    }

    .dial-label {
        position: absolute;
        bottom: 4px;
        left: 0;
        right: 0;
        color: var(--site-text-muted);
        font-size: 0.8rem;
        letter-spacing: 2px;
        text-transform: uppercase;
    }
}
</style>
